(() => {
  'use strict';

  const ROOT_SELECTOR = '.ah-customizer[data-ah-customizer]';
  const FABRIC_WAIT_MAX = 12000;
  const BUILD = '7.1';
  const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  function cmToIn(cm) { return cm / 2.54; }
  function uid() { return 'AH-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).slice(2, 8).toUpperCase(); }
  function money(cents, currency) {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100); }
    catch (_) { return (cents / 100).toFixed(2) + ' €'; }
  }
  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }
  function loadHtmlImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }
  function canvasToBlob(canvasEl, type = 'image/jpeg', quality = 0.94) {
    return new Promise((resolve, reject) => {
      try {
        canvasEl.toBlob(blob => blob ? resolve(blob) : reject(new Error('Impossible de générer le fichier final.')), type, quality);
      } catch (e) { reject(e); }
    });
  }
  function parseSize(value) {
    if (!value) return null;
    const text = String(value).normalize('NFKC').replace(/,/g, '.');
    const sizes = [...text.matchAll(/(\d+(?:\.\d+)?)\s*(cm|mm|inches|inch|in|["″])?\s*[x×X✕*]\s*(\d+(?:\.\d+)?)\s*(cm|mm|inches|inch|in|["″])?/gi)];
    const m = sizes.find(match => /^(cm|mm)$/i.test(match[2] || match[4] || '')) || sizes[0];
    if (!m) return null;
    const convert = (number, unit) => Number(number) * (unit === 'mm' ? .1 : /^(inches|inch|in|["″])$/i.test(unit) ? 2.54 : 1);
    const a = Number(convert(m[1], (m[2] || m[4] || 'cm').toLowerCase()).toFixed(4));
    const b = Number(convert(m[3], (m[4] || m[2] || 'cm').toLowerCase()).toFixed(4));
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
    return { a, b, label: `${a} × ${b} cm` };
  }
  function safeText(v) { return String(v || '').replace(/[<>]/g, ''); }

  async function waitForFabric() {
    const start = Date.now();
    while (!window.fabric) {
      if (Date.now() - start > FABRIC_WAIT_MAX) throw new Error('Le module de personnalisation ne s’est pas chargé. Rechargez la page.');
      await new Promise(r => setTimeout(r, 50));
    }
    return window.fabric;
  }

  class AHCustomizer {
    constructor(root, fabric) {
      this.root = root;
      this.fabric = fabric;
      this.minDpi = Number(root.dataset.minDpi || 150);
      this.recDpi = Number(root.dataset.recDpi || 200);
      this.exportDpi = Number(root.dataset.exportDpi || 150);
      this.maxExportMp = Number(root.dataset.maxExportMp || 19);
      this.maxImages = Number(root.dataset.maxImages || 8);
      this.absoluteMinPx = Number(root.dataset.absoluteMinPx || 800);
      this.maxInputMb = Number(root.dataset.maxInputMb || 20);
      this.defaultWidthCm = Number(root.dataset.defaultWidthCm || 50);
      this.defaultHeightCm = Number(root.dataset.defaultHeightCm || 70);
      this.frameWidthCm = clamp(Number(root.dataset.frameWidthMm) || 15, 5, 50) / 10;
      this.frameDepthCm = clamp(Number(root.dataset.frameDepthMm) || 20, 5, 60) / 10;
      this.currency = root.dataset.currency || 'EUR';
      this.designId = uid();
      this.orientation = null;
      this.selectedOptions = [];
      this.selectedVariant = null;
      this.assets = new Map();
      this.history = [];
      this.historyIndex = -1;
      this.loadingHistory = false;
      this.saving = false;
      this.bgColor = '#ffffff';
      this.sourceProduct = new URLSearchParams(location.search).get('source') || '';
      this.product = JSON.parse(root.querySelector('[data-ah-product-json]').textContent);
      this.els = this.collectEls();
      this.initVariantState();
      this.initCanvas();
      this.renderOptionGroups();
      this.initTabs();
      this.initReview();
      this.bind();
      this.resizeCanvasForCurrentFormat(false);
      this.updateRequirements();
      this.updateSummary();
      this.updateQuality();
      this.pushHistory();
    }

    collectEls() {
      const q = s => this.root.querySelector(s);
      const qa = s => [...this.root.querySelectorAll(s)];
      return {
        editorShell: q('.ah-editor-shell'),
        framePreview: q('[data-ah-frame-preview]'),
        review: q('[data-ah-review]'),
        reviewTitle: q('[data-ah-review-title]'),
        reviewImage: q('[data-ah-review-image]'),
        reviewFront: q('[data-ah-review-front]'),
        reviewScene: q('[data-ah-review-scene]'),
        reviewModel: q('[data-ah-review-model]'),
        reviewViewport: q('[data-ah-review-viewport]'),
        reviewDimensions: q('[data-ah-review-dimensions]'),
        reviewFormat: q('[data-ah-review-format]'),
        reviewOrientation: q('[data-ah-review-orientation]'),
        reviewFinish: q('[data-ah-review-finish]'),
        reviewQuality: q('[data-ah-review-quality]'),
        reviewHint: q('[data-ah-review-hint]'),
        reviewRotation: q('[data-ah-review-rotation]'),
        reviewRotate: qa('[data-ah-rotate]'),
        reviewViews: qa('[data-ah-review-view]'),
        reviewEdit: qa('[data-ah-review-edit]'),
        reviewAdd: q('[data-ah-review-add]'),
        reviewStatus: q('[data-ah-review-status]'),
        optionGroups: q('[data-ah-option-groups]'),
        orientation: q('[data-ah-orientation]'),
        fileInput: q('[data-ah-file-input]'),
        fileList: q('[data-ah-file-list]'),
        photoCount: q('[data-ah-photo-count]'),
        reqMin: q('[data-ah-req-min]'),
        reqRec: q('[data-ah-req-rec]'),
        reqActual: q('[data-ah-export-actual]'),
        canvas: q('[data-ah-canvas]'),
        stage: q('[data-ah-stage]'),
        stageWrap: q('[data-ah-stage-wrap]'),
        dropzone: q('[data-ah-dropzone]'),
        quality: q('[data-ah-quality]'),
        qualityGlobal: q('[data-ah-quality-global]'),
        summaryFormat: q('[data-ah-summary-format]'),
        summaryOrientation: q('[data-ah-summary-orientation]'),
        summaryVariant: q('[data-ah-summary-variant]'),
        prices: qa('[data-ah-price]'),
        add: q('[data-ah-add]'),
        status: q('[data-ah-status]'),
        consent: q('[data-ah-consent]'),
        textInput: q('[data-ah-text-input]'),
        addText: q('[data-ah-add-text]'),
        draw: q('[data-ah-draw]'),
        drawLabel: q('[data-ah-draw-label]'),
        penColor: q('[data-ah-pen-color]'),
        penWidth: q('[data-ah-pen-width]'),
        del: q('[data-ah-delete]'),
        forward: q('[data-ah-forward]'),
        backward: q('[data-ah-backward]'),
        fit: q('[data-ah-fit]'),
        center: q('[data-ah-center]'),
        undo: q('[data-ah-undo]'),
        redo: q('[data-ah-redo]'),
        bg: q('[data-ah-bg]'),
        clear: q('[data-ah-clear]'),
        toolrail: q('[data-ah-toolrail]'),
        tabButtons: qa('[data-ah-tab]'),
        tabPanels: qa('[data-ah-panel]'),
        panelTitle: q('[data-ah-panel-title]'),
        panelClose: q('[data-ah-panel-close]'),
        back: q('[data-ah-back]'),
        mobileReview: qa('[data-ah-mobile-review]'),
        colorPresets: qa('[data-ah-color-presets] [data-color]')
      };
    }

    initVariantState() {
      const first = this.product.variants.find(v => v.available) || this.product.variants[0];
      if (!first) throw new Error('Aucune variante n’est disponible pour le produit sélectionné.');
      this.selectedOptions = [...first.options];
      this.selectedVariant = first;
    }

    initCanvas() {
      this.fabric.Object.prototype.transparentCorners = false;
      this.fabric.Object.prototype.cornerStyle = 'circle';
      this.fabric.Object.prototype.cornerColor = '#ffffff';
      this.fabric.Object.prototype.cornerStrokeColor = '#6c45ff';
      this.fabric.Object.prototype.borderColor = '#6c45ff';
      this.fabric.Object.prototype.cornerSize = 10;
      this.fabric.Object.prototype.padding = 2;
      this.fabric.Object.prototype.borderScaleFactor = 1.2;
      this.canvas = new this.fabric.Canvas(this.els.canvas, {
        preserveObjectStacking: true,
        selection: true,
        allowTouchScrolling: false,
        selectionColor: 'rgba(108,69,255,.08)',
        selectionBorderColor: '#6c45ff',
        backgroundColor: this.bgColor,
        skipOffscreen: false,
        renderOnAddRemove: true
      });
      this.canvas.setWidth(520);
      this.canvas.setHeight(700);
      if (this.canvas.upperCanvasEl) {
        this.canvas.upperCanvasEl.style.touchAction = 'none';
        this.canvas.upperCanvasEl.style.webkitUserSelect = 'none';
        this.canvas.upperCanvasEl.style.userSelect = 'none';
      }
      if (this.canvas.lowerCanvasEl) {
        this.canvas.lowerCanvasEl.style.touchAction = 'none';
      }
      this.canvas.freeDrawingBrush = new this.fabric.PencilBrush(this.canvas);
      this.canvas.freeDrawingBrush.color = this.els.penColor.value || '#2d2a24';
      this.canvas.freeDrawingBrush.width = Number(this.els.penWidth.value || 4);
      this.canvas.on('object:added', e => { if (!this.loadingHistory && !e.target?._skipHistory) this.debouncedHistory(); this.updateQuality(); });
      this.canvas.on('object:modified', () => { this.pushHistory(); this.updateQuality(); });
      this.canvas.on('object:removed', () => { if (!this.loadingHistory) this.debouncedHistory(); this.updateQuality(); });
      this.canvas.on('path:created', () => { this.pushHistory(); this.updateQuality(); });
      this.canvas.on('text:editing:exited', () => this.pushHistory());
      this.canvas.on('selection:created', () => this.syncSelectedAsset());
      this.canvas.on('selection:updated', () => this.syncSelectedAsset());
      this.canvas.on('selection:cleared', () => this.syncSelectedAsset());
      this.canvas.on('mouse:down', () => { this._canvasGesture = true; });
      this.canvas.on('mouse:up', () => { this._canvasGesture = false; this.recoverCanvas(); });
      ['object:moving','object:scaling','object:rotating'].forEach(eventName => {
        this.canvas.on(eventName, event => {
          this._canvasGesture = true;
          if (event.target) event.target.setCoords();
          this.canvas.requestRenderAll();
        });
      });
      const finishTouch = () => {
        this._canvasGesture = false;
        requestAnimationFrame(() => this.recoverCanvas());
      };
      const upperCanvas = this.canvas.upperCanvasEl;
      upperCanvas?.addEventListener('touchstart', () => { this._canvasGesture = true; }, {passive:true});
      upperCanvas?.addEventListener('touchmove', event => {
        if (event.cancelable) event.preventDefault();
        this.canvas.requestRenderAll();
      }, {passive:false});
      upperCanvas?.addEventListener('touchcancel', finishTouch, {passive:true});
      upperCanvas?.addEventListener('touchend', finishTouch, {passive:true});
      window.addEventListener('resize', () => { this.scheduleCanvasFit(); if (this.reviewOpen) this.layoutReview(); });
      window.addEventListener('pageshow', () => this.recoverCanvas());
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.recoverCanvas(); });
      if ('ResizeObserver' in window) {
        this._stageResizeObserver = new ResizeObserver(() => this.scheduleCanvasFit());
        if (this.els.stageWrap) this._stageResizeObserver.observe(this.els.stageWrap);
      }
    }

    initTabs() {
      const titles = {
        photos: 'Ajouter vos photos',
        text: 'Ajouter du texte',
        draw: 'Dessiner librement',
        format: 'Format & finition',
        background: 'Couleur du fond'
      };
      this.els.tabButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.ahTab;
          this.root.classList.remove('ah-panel-collapsed');
          this.els.tabButtons.forEach(b => b.classList.toggle('is-active', b === btn));
          this.els.tabPanels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.ahPanel === key));
          if (this.els.panelTitle) this.els.panelTitle.textContent = titles[key] || 'Personnaliser';
        });
      });
      this.els.panelClose?.addEventListener('click', () => this.root.classList.add('ah-panel-collapsed'));
      this.els.back?.addEventListener('click', () => history.back());
      this.els.mobileReview.forEach(btn => btn.addEventListener('click', () => this.openReview()));
      this.els.colorPresets.forEach(btn => btn.addEventListener('click', () => {
        const color = btn.dataset.color;
        if (!color) return;
        this.els.bg.value = color;
        this.bgColor = color;
        this.canvas.setBackgroundColor(color, () => this.canvas.requestRenderAll());
        this.els.colorPresets.forEach(b => b.classList.toggle('is-active', b === btn));
        this.pushHistory();
      }));
      this.bindDropTargets();
    }

    bindDropTargets() {
      const targets = [this.els.dropzone, this.els.stageWrap].filter(Boolean);
      targets.forEach(target => {
        target.addEventListener('dragover', e => { e.preventDefault(); target.classList.add('is-dragover'); });
        target.addEventListener('dragleave', e => {
          if (!target.contains(e.relatedTarget)) target.classList.remove('is-dragover');
        });
        target.addEventListener('drop', e => {
          e.preventDefault();
          target.classList.remove('is-dragover');
          const files = [...(e.dataTransfer?.files || [])].filter(file => file.type.startsWith('image/'));
          if (files.length) this.handleFiles(files);
        });
      });
    }

    bind() {
      this.els.orientation?.addEventListener('click', e => {
        const btn = e.target.closest('[data-value]');
        if (!btn || btn.disabled) return;
        this.orientation = btn.dataset.value;
        [...this.els.orientation.querySelectorAll('[data-value]')].forEach(b => b.classList.toggle('is-active', b === btn));
        this.resizeCanvasForCurrentFormat(true);
        this.updateRequirements();
        this.updateSummary();
        this.updateQuality();
      });

      this.els.fileInput?.addEventListener('change', e => this.handleFiles([...e.target.files]));
      this.els.addText?.addEventListener('click', () => this.addText());
      this.els.textInput?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.addText(); } });
      this.els.draw?.addEventListener('click', () => this.toggleDraw());
      this.els.penColor?.addEventListener('input', () => { this.canvas.freeDrawingBrush.color = this.els.penColor.value; });
      this.els.penWidth?.addEventListener('input', () => { this.canvas.freeDrawingBrush.width = Number(this.els.penWidth.value); });
      this.els.del?.addEventListener('click', () => this.deleteSelection());
      this.els.forward?.addEventListener('click', () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.bringForward(o); this.canvas.requestRenderAll(); this.pushHistory(); } });
      this.els.backward?.addEventListener('click', () => { const o = this.canvas.getActiveObject(); if (o) { this.canvas.sendBackwards(o); this.canvas.requestRenderAll(); this.pushHistory(); } });
      this.els.fit?.addEventListener('click', () => this.fitSelectedImage());
      this.els.center?.addEventListener('click', () => { const o = this.canvas.getActiveObject(); if (o) { o.center(); o.setCoords(); this.canvas.requestRenderAll(); this.pushHistory(); this.updateQuality(); } });
      this.els.undo?.addEventListener('click', () => this.undo());
      this.els.redo?.addEventListener('click', () => this.redo());
      this.els.bg?.addEventListener('input', () => { this.bgColor = this.els.bg.value; this.canvas.setBackgroundColor(this.bgColor, () => this.canvas.requestRenderAll()); this.els.colorPresets.forEach(b => b.classList.toggle('is-active', b.dataset.color?.toLowerCase() === this.bgColor.toLowerCase())); this.pushHistory(); });
      this.els.clear?.addEventListener('click', () => this.clearDesign());
      this.els.add?.addEventListener('click', () => this.openReview());
      this.els.consent?.addEventListener('change', () => this.refreshAddState());
      document.addEventListener('keydown', e => {
        if (!this.root.isConnected || this.reviewOpen || this.saving || this.preparingReview) return;
        const tag = document.activeElement?.tagName;
        if ((e.key === 'Delete' || e.key === 'Backspace') && !['INPUT','TEXTAREA'].includes(tag)) { e.preventDefault(); this.deleteSelection(); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); }
      });
    }

    renderOptionGroups() {
      this.els.optionGroups.innerHTML = '';
      this.product.options.forEach((opt, idx) => {
        const wrap = document.createElement('div');
        wrap.className = 'ah-block';
        const h = document.createElement('h3'); h.textContent = opt.name;
        const list = document.createElement('div'); list.className = 'ah-options';
        const values = [...new Set(this.product.variants.map(v => v.options[idx]))];
        values.forEach(value => {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'ah-option'; b.textContent = value; b.dataset.optionIndex = idx; b.dataset.value = value;
          if (this.selectedOptions[idx] === value) b.classList.add('is-active');
          b.addEventListener('click', () => this.selectOption(idx, value));
          list.appendChild(b);
        });
        wrap.append(h, list);
        this.els.optionGroups.appendChild(wrap);
      });
      this.updateOptionAvailability();
    }

    selectOption(index, value) {
      this.selectedOptions[index] = value;
      let v = this.findExactVariant(this.selectedOptions);
      if (!v || !v.available) {
        v = this.product.variants.find(candidate => candidate.available && candidate.options[index] === value && candidate.options.every((x, i) => i === index || x === this.selectedOptions[i]))
          || this.product.variants.find(candidate => candidate.available && candidate.options[index] === value);
        if (v) this.selectedOptions = [...v.options];
      }
      if (v) this.selectedVariant = v;
      if (parseSize(value)) this.orientation = null;
      this.syncOptionButtons();
      this.updateOptionAvailability();
      this.resizeCanvasForCurrentFormat(true);
      this.updateRequirements();
      this.updateSummary();
      this.updateQuality();
    }

    findExactVariant(options) { return this.product.variants.find(v => v.options.every((x, i) => x === options[i])); }

    syncOptionButtons() {
      this.els.optionGroups.querySelectorAll('[data-option-index]').forEach(b => {
        const i = Number(b.dataset.optionIndex);
        b.classList.toggle('is-active', this.selectedOptions[i] === b.dataset.value);
      });
    }

    updateOptionAvailability() {
      this.els.optionGroups.querySelectorAll('[data-option-index]').forEach(btn => {
        const idx = Number(btn.dataset.optionIndex), value = btn.dataset.value;
        const possible = this.product.variants.some(v => {
          if (!v.available || v.options[idx] !== value) return false;
          return v.options.every((x, i) => i === idx || this.selectedOptions[i] === x || true);
        });
        btn.disabled = !possible;
      });
    }

    getFrame() {
      const options = this.selectedVariant?.options || this.selectedOptions;
      const index = this.product.options.findIndex(opt => /cadre|frame|finition|finish/i.test(opt.name));
      const label = index >= 0 ? String(options[index] || '') : String(options.find(value => /cadre|frame/i.test(value)) || 'Sans cadre');
      const normalized = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const none = /sans\s*cadre|sans\s*encadrement|non\s*encadre|no\s*frame|without\s*frame|unframed|frameless|rolled|roule|^aucun$|^none$/.test(normalized);
      const palettes = [
        [/noir|black/, 'black', '#222222', '#121212'],
        [/blanc|white/, 'white', '#f4f2ec', '#d8d5ce'],
        [/fonce|dark|noyer|walnut|marron|brown/, 'walnut', '#71503a', '#493324'],
        [/bois|wood|chene|oak|naturel|natural|beige|clair/, 'wood', '#d5b58a', '#9b7952'],
        [/dore|gold|\bor\b/, 'gold', '#c3a36a', '#8a6b3a'],
        [/argent|silver|aluminium|aluminum/, 'silver', '#c7c7c7', '#8d8d8d']
      ];
      const match = palettes.find(([pattern]) => pattern.test(normalized));
      const enabled = !none && (!!match || /cadre|frame|encadre/.test(normalized));
      return { enabled, label: label || 'Sans cadre', tone: enabled ? (match?.[1] || 'neutral') : 'none', color: match?.[2] || '#b6aa99', edge: match?.[3] || '#82766a', widthCm: enabled ? this.frameWidthCm : 0, depthCm: enabled ? this.frameDepthCm : .15 };
    }

    applyFrameAppearance(element, frame) {
      element.dataset.frame = frame.tone;
      element.style.setProperty('--ah-frame-color', frame.color);
      element.style.setProperty('--ah-frame-edge', frame.edge);
    }

    updateFramePreview() {
      const frame = this.getFrame();
      this.applyFrameAppearance(this.els.stage, frame);
      this.els.framePreview.hidden = !frame.enabled;
      this.scheduleCanvasFit();
    }

    scheduleCanvasFit() {
      if (this._fitRaf) return;
      this._fitRaf = requestAnimationFrame(() => {
        this._fitRaf = null;
        if (!this.root.isConnected || this.reviewOpen) return;
        if (this._canvasGesture) { this._fitPending = true; return; }
        this._fitPending = false;
        this.fitCanvasCss();
      });
    }

    recoverCanvas() {
      if (!this.root.isConnected || this.reviewOpen) return;
      this.canvas.calcOffset();
      this.canvas.requestRenderAll();
      this.scheduleCanvasFit();
    }

    initReview() {
      if (this._reviewBound) return true;
      this.reviewOpen = false;
      this.reviewMode = 'front';
      this.reviewRotation = { x: -8, y: -24 };

      // V7.1: do not crash if Shopify is still rendering the section or if an old Liquid file is cached.
      const required = ['review','reviewImage','reviewFront','reviewScene','reviewModel','reviewViewport','reviewAdd','reviewStatus'];
      const missing = required.filter(key => !this.els[key]);
      if (missing.length) {
        this.reviewAvailable = false;
        console.warn(`[Ambient Home personnalisateur ${BUILD}] Écran de vérification indisponible :`, missing.join(', '));
        return false;
      }

      this.reviewAvailable = true;
      this._reviewBound = true;
      this.els.reviewEdit.forEach(button => button.addEventListener('click', () => this.leaveReview()));
      this.els.reviewAdd?.addEventListener('click', () => this.finalizeAndAdd());
      this.els.reviewViews.forEach(button => button.addEventListener('click', () => this.setReviewView(button.dataset.ahReviewView)));
      this.els.reviewRotate.forEach(button => button.addEventListener('click', () => this.rotateReview(button.dataset.ahRotate)));
      const viewport = this.els.reviewViewport;
      viewport?.addEventListener('keydown', event => {
        if (this.reviewMode !== '3d') return;
        const directions = { ArrowLeft:'left', ArrowRight:'right', ArrowUp:'up', ArrowDown:'down', Home:'reset' };
        if (directions[event.key]) { event.preventDefault(); this.rotateReview(directions[event.key]); }
      });
      viewport?.addEventListener('pointerdown', event => {
        if (this.reviewMode !== '3d' || (event.pointerType === 'mouse' && event.button !== 0) || this._reviewPointer) return;
        this._reviewPointer = { id:event.pointerId, x:event.clientX, y:event.clientY, rotation:{...this.reviewRotation} };
        viewport.setPointerCapture(event.pointerId);
      });
      viewport?.addEventListener('pointermove', event => {
        const pointer = this._reviewPointer;
        if (!pointer || pointer.id !== event.pointerId) return;
        this.reviewRotation = { x:clamp(pointer.rotation.x - (event.clientY-pointer.y)*.35, -55, 55), y:clamp(pointer.rotation.y + (event.clientX-pointer.x)*.4, -75, 75) };
        this.applyReviewRotation();
      });
      const finish = () => { this._reviewPointer = null; };
      viewport?.addEventListener('pointerup', finish);
      viewport?.addEventListener('pointercancel', finish);
      viewport?.addEventListener('lostpointercapture', finish);
      this._popReview = () => {
        if (this.saving || this.addedToCart) return;
        if (history.state?.ahReview === this.designId && this.reviewSignature === this.serialize()) this.presentReview(false);
        else if (this.reviewOpen) this.leaveReview(false);
      };
      window.addEventListener('popstate', this._popReview);
      if ('ResizeObserver' in window) {
        this._reviewResizeObserver = new ResizeObserver(() => {
          if (!this.reviewOpen || this._reviewLayoutRaf) return;
          this._reviewLayoutRaf = requestAnimationFrame(() => { this._reviewLayoutRaf = null; if (this.reviewOpen) this.layoutReview(); });
        });
        if (viewport) this._reviewResizeObserver.observe(viewport);
      }
      return true;
    }

    async openReview() {
      if (!this.reviewAvailable) {
        this.els = this.collectEls();
        if (!this.initReview()) {
          this.showStatus('Le JavaScript V7.1 est chargé, mais le bloc de vérification est absent. Remplacez aussi le fichier sections/tableau-personnalise.liquid fourni dans le ZIP, puis rechargez la page.', 'error');
          return;
        }
      }
      if (this.reviewOpen || this.saving || this.addedToCart || this.preparingReview || this.importing || this.loadingHistory) return;
      this.updateQuality();
      if (!this.canvas.getObjects().length) { this.showStatus('Ajoutez une image, un texte ou un dessin pour vérifier votre tableau.', 'info'); return; }
      if (!this.selectedVariant?.available) { this.showStatus('Choisissez une finition disponible.', 'error'); return; }
      this.preparingReview = true;
      this.refreshAddState();
      this.clearStatus();
      try {
        const active = this.canvas.getActiveObject();
        if (active?.isEditing) active.exitEditing();
        this.canvas.isDrawingMode = false;
        this.els.draw.classList.remove('is-active');
        if (this.els.drawLabel) this.els.drawLabel.textContent = 'Activer le stylo';
        this.canvas.discardActiveObject();
        this.canvas.renderAll();
        this.pushHistory();
        const signature = this.serialize();
        const rendered = this.canvas.toCanvasElement(Math.min(2, 1200 / Math.max(this.canvas.getWidth(), this.canvas.getHeight())));
        const blob = await canvasToBlob(rendered, 'image/jpeg', .95);
        rendered.width = 0; rendered.height = 0;
        if (signature !== this.serialize()) throw new Error('Votre création a changé. Cliquez de nouveau sur « Vérifier mon tableau ».');
        const url = URL.createObjectURL(blob);
        try { await loadHtmlImage(url); } catch (error) { URL.revokeObjectURL(url); throw error; }
        if (this.reviewUrl) URL.revokeObjectURL(this.reviewUrl);
        this.reviewUrl = url;
        this.reviewSignature = signature;
        this.els.reviewImage.src = url;
        this.els.consent.checked = false;
        this.reviewRotation = { x:-8, y:-24 };
        this.setReviewView('front');
        this.presentReview(true);
      } catch (error) {
        this.showStatus(error.message || 'L’aperçu n’a pas pu être créé. Votre création est conservée ; réessayez.', 'error');
      } finally {
        this.preparingReview = false;
        this.refreshAddState();
        if (!this.reviewOpen) this.recoverCanvas();
      }
    }

    presentReview(pushHistory) {
      if (!this.reviewUrl) return;
      if (pushHistory) {
        this._editorUrl = location.href;
        this._editorHistoryState = history.state;
        const next = new URL(location.href);
        next.hash = 'verification-tableau';
        history.pushState({ ...(history.state || {}), ahReview:this.designId }, '', next);
      }
      this.reviewOpen = true;
      this.root.classList.add('ah-is-review');
      this.els.review.hidden = false;
      this.els.editorShell.inert = true;
      this.updateReviewDetails();
      this.layoutReview();
      this.refreshAddState();
      this.els.reviewTitle.focus({preventScroll:true});
      this.els.review.scrollIntoView({behavior:'auto',block:'start'});
    }

    leaveReview(updateHistory = true) {
      if (this.saving || this.addedToCart) return;
      this.reviewOpen = false;
      this.root.classList.remove('ah-is-review');
      this.els.review.hidden = true;
      this.els.editorShell.inert = false;
      if (updateHistory && history.state?.ahReview === this.designId) {
        history.replaceState(this._editorHistoryState || null, '', this._editorUrl || location.pathname + location.search);
      }
      this.clearStatus();
      this.refreshAddState();
      this.els.add.focus({preventScroll:true});
      this.els.stageWrap.scrollIntoView({behavior:'auto',block:'center'});
      this.recoverCanvas();
    }

    updateReviewDetails() {
      const size = this.getPrintSize(), frame = this.getFrame();
      const label = `${size.widthCm} × ${size.heightCm} cm`;
      this.els.reviewFormat.textContent = label;
      this.els.reviewOrientation.textContent = this.getOrientationLabel();
      this.els.reviewFinish.textContent = frame.label;
      this.els.reviewDimensions.textContent = `Largeur ${size.widthCm} cm · Hauteur ${size.heightCm} cm · Zone imprimée`;
      const images = this.canvas.getObjects().filter(object => object.type === 'image');
      const minimum = images.length ? Math.min(...images.map(object => this.getImageDpi(object))) : null;
      this.els.reviewQuality.textContent = minimum === null ? 'Création sans photo' : this.hasBlockingQuality ? 'À corriger avant commande' : minimum < this.recDpi ? 'Définition correcte' : 'Très bonne définition';
      this.els.reviewQuality.classList.toggle('is-bad', !!this.hasBlockingQuality);
      if (this.hasBlockingQuality) this.showStatus('Une image manque de définition. Revenez à la modification pour la réduire ou la remplacer.', 'error');
      this.applyFrameAppearance(this.els.reviewFront, frame);
      this.applyFrameAppearance(this.els.reviewModel, frame);
    }

    setReviewView(mode) {
      this.reviewMode = mode === '3d' ? '3d' : 'front';
      const is3d = this.reviewMode === '3d';
      this.els.reviewFront.hidden = is3d;
      this.els.reviewScene.hidden = !is3d;
      this.els.reviewRotation.hidden = !is3d;
      this.els.reviewViewport.classList.toggle('is-3d', is3d);
      this.els.reviewViews.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ahReviewView === this.reviewMode)));
      this.els.reviewHint.textContent = is3d ? 'Glissez sur le tableau pour le faire pivoter, ou utilisez les flèches.' : 'Vérifiez les bords et la position de chaque élément.';
      if (this.reviewOpen) this.layoutReview();
    }

    rotateReview(direction) {
      const rotation = this.reviewRotation;
      if (direction === 'reset') this.reviewRotation = { x:-8, y:-24 };
      else if (direction === 'left') rotation.y = clamp(rotation.y - 12,-75,75);
      else if (direction === 'right') rotation.y = clamp(rotation.y + 12,-75,75);
      else if (direction === 'up') rotation.x = clamp(rotation.x - 10,-55,55);
      else if (direction === 'down') rotation.x = clamp(rotation.x + 10,-55,55);
      this.applyReviewRotation();
    }

    applyReviewRotation() {
      this.els.reviewModel.style.transform = `rotateX(${this.reviewRotation.x}deg) rotateY(${this.reviewRotation.y}deg)`;
    }

    layoutReview() {
      if (!this.reviewOpen || !this.els.reviewViewport.clientWidth) return;
      const size = this.getPrintSize(), frame = this.getFrame();
      const outerWidth = size.widthCm + 2*frame.widthCm, outerHeight = size.heightCm + 2*frame.widthCm;
      const view = this.els.reviewViewport;
      const factor = this.reviewMode === '3d' ? .69 : .86;
      const scale = Math.min(view.clientWidth*factor/outerWidth, view.clientHeight*factor/outerHeight);
      const width = size.widthCm*scale, height = size.heightCm*scale, border = frame.widthCm*scale;
      const front = this.els.reviewFront;
      front.style.width = `${width+2*border}px`;
      front.style.height = `${height+2*border}px`;
      front.style.padding = `${border}px`;
      this.els.reviewImage.width = Math.max(1,Math.round(width));
      this.els.reviewImage.height = Math.max(1,Math.round(height));
      if (this.reviewMode !== '3d') return;
      const model = this.els.reviewModel;
      model.style.width = `${width+2*border}px`;
      model.style.height = `${height+2*border}px`;
      model.replaceChildren();
      const depth = Math.max(.5, frame.depthCm*scale);
      const boardDepth = frame.enabled ? Math.min(depth*.4, .3*scale) : depth;
      this.createReviewBox(width,height,boardDepth,border,border,frame.enabled ? (depth-boardDepth)/2-.05*scale : 0,'art',this.reviewUrl);
      if (frame.enabled) {
        this.createReviewBox(width+2*border,border,depth,0,0,0,'frame');
        this.createReviewBox(width+2*border,border,depth,0,height+border,0,'frame');
        this.createReviewBox(border,height,depth,0,border,0,'frame');
        this.createReviewBox(border,height,depth,width+border,border,0,'frame');
      }
      this.applyReviewRotation();
    }

    createReviewBox(width,height,depth,left,top,z,material,imageUrl) {
      const box = document.createElement('div');
      box.className = `ah-3d-box ah-3d-box--${material}`;
      Object.assign(box.style,{width:`${width}px`,height:`${height}px`,left:`${left}px`,top:`${top}px`,transform:`translateZ(${z}px)`});
      const faces = [
        ['front',width,height,0,0,`translateZ(${depth/2}px)`],
        ['back',width,height,0,0,`rotateY(180deg) translateZ(${depth/2}px)`],
        ['left',depth,height,(width-depth)/2,0,`rotateY(-90deg) translateZ(${width/2}px)`],
        ['right',depth,height,(width-depth)/2,0,`rotateY(90deg) translateZ(${width/2}px)`],
        ['top',width,depth,0,(height-depth)/2,`rotateX(90deg) translateZ(${height/2}px)`],
        ['bottom',width,depth,0,(height-depth)/2,`rotateX(-90deg) translateZ(${height/2}px)`]
      ];
      faces.forEach(([side,w,h,x,y,transform]) => {
        const face = document.createElement('div');
        face.className = `ah-3d-face ah-3d-face--${side}`;
        Object.assign(face.style,{width:`${w}px`,height:`${h}px`,left:`${x}px`,top:`${y}px`,transform});
        if (imageUrl && side === 'front') {
          const image = document.createElement('img');
          image.src = imageUrl; image.alt = 'Votre création en trois dimensions'; image.draggable = false;
          face.appendChild(image);
        }
        box.appendChild(face);
      });
      this.els.reviewModel.appendChild(box);
    }

    getPrintSize() {
      const options = this.selectedVariant?.options || this.selectedOptions;
      const index = this.product.options.findIndex(opt => /format|taille|size|dimension/i.test(opt.name));
      let parsed = index >= 0 ? parseSize(options[index]) : null;
      if (!parsed) for (const value of options) { parsed = parseSize(value); if (parsed) break; }
      if (!parsed) parsed = parseSize(this.selectedVariant?.title);
      const a = parsed?.a || this.defaultWidthCm, b = parsed?.b || this.defaultHeightCm;
      const orientation = this.orientation || (a > b ? 'landscape' : 'portrait');
      return {
        widthCm: orientation === 'landscape' ? Math.max(a,b) : Math.min(a,b),
        heightCm: orientation === 'landscape' ? Math.min(a,b) : Math.max(a,b),
        raw: parsed?.label || `${a} × ${b} cm`
      };
    }

    getOrientationLabel() {
      const s = this.getPrintSize();
      return s.widthCm === s.heightCm ? 'Carré' : s.widthCm > s.heightCm ? 'Paysage' : 'Portrait';
    }

    resizeCanvasForCurrentFormat(rescaleObjects) {
      const size = this.getPrintSize();
      const ratio = size.widthCm / size.heightCm;
      const maxSide = 700;
      let newW, newH;
      if (ratio >= 1) { newW = maxSide; newH = maxSide / ratio; }
      else { newH = maxSide; newW = maxSide * ratio; }
      const oldW = this.canvas.getWidth(), oldH = this.canvas.getHeight();
      if (rescaleObjects && oldW && oldH && this.canvas.getObjects().length) {
        const sx = newW / oldW, sy = newH / oldH, uni = Math.min(sx, sy);
        this.canvas.getObjects().forEach(o => {
          o.set({ left: (o.left || 0) * sx, top: (o.top || 0) * sy, scaleX: (o.scaleX || 1) * uni, scaleY: (o.scaleY || 1) * uni });
          o.setCoords();
        });
      }
      this._cssSizeKey = null;
      this.canvas.setDimensions({ width: newW, height: newH });
      this.canvas.setBackgroundColor(this.bgColor, () => this.canvas.requestRenderAll());
      this.fitCanvasCss();
      if (rescaleObjects) this.pushHistory();
    }

    fitCanvasCss() {
      // Fit against the real workspace, not the canvas' own min-content parent.
      // This avoids the oversized canvas + horizontal/vertical scroll bug.
      const host = this.els.stageWrap;
      if (!host || !this.canvas || this.reviewOpen || !host.clientWidth || !host.clientHeight) return;

      const cs = getComputedStyle(host);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const baseW = Number(this.canvas.getWidth()) || 1;
      const baseH = Number(this.canvas.getHeight()) || 1;
      const availableW = Math.max(1, host.clientWidth - padX - 8);
      // Reserve a little room for the print label and helper line.
      const occupied = ['.ah-stage-label', '.ah-stage-hint'].reduce((height, selector) => {
        const el = host.querySelector(selector);
        if (!el) return height;
        const style = getComputedStyle(el);
        return height + el.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }, 8);
      const availableH = Math.max(1, host.clientHeight - padY - occupied);
      const frame = this.getFrame();
      const size = this.getPrintSize();
      const borderBase = frame.widthCm * baseW / size.widthCm;
      const scale = Math.min(1, availableW / (baseW+2*borderBase), availableH / (baseH+2*borderBase));
      const cssW = baseW * scale;
      const cssH = baseH * scale;

      this.els.stage.style.setProperty('--ah-frame-size', `${borderBase*scale}px`);
      const key = `${cssW.toFixed(3)}x${cssH.toFixed(3)}`;
      if (this._cssSizeKey === key) { this.canvas.calcOffset(); this.canvas.requestRenderAll(); return; }
      this._cssSizeKey = key;

      // Fabric keeps a wrapper around the two canvases; force all three to the same visual size.
      const wrapper = this.canvas.wrapperEl;
      if (wrapper) {
        wrapper.style.width = cssW + 'px';
        wrapper.style.height = cssH + 'px';
        wrapper.style.maxWidth = '100%';
        wrapper.style.maxHeight = '100%';
      }
      [this.canvas.lowerCanvasEl, this.canvas.upperCanvasEl].filter(Boolean).forEach(el => {
        el.style.width = cssW + 'px';
        el.style.height = cssH + 'px';
        el.style.maxWidth = '100%';
        el.style.maxHeight = '100%';
      });
      this.els.stage.style.width = cssW + 'px';
      this.els.stage.style.height = cssH + 'px';
      this.canvas.calcOffset();
      this.canvas.requestRenderAll();
    }

    updateRequirements() {
      const s = this.getPrintSize();
      const minW = Math.round(cmToIn(s.widthCm) * this.minDpi), minH = Math.round(cmToIn(s.heightCm) * this.minDpi);
      const recW = Math.round(cmToIn(s.widthCm) * this.recDpi), recH = Math.round(cmToIn(s.heightCm) * this.recDpi);
      this.els.reqMin.textContent = `${minW.toLocaleString('fr-FR')} × ${minH.toLocaleString('fr-FR')} px`;
      this.els.reqRec.textContent = `${recW.toLocaleString('fr-FR')} × ${recH.toLocaleString('fr-FR')} px`;
      const exp = this.computeExportSpec();
      this.els.reqActual.textContent = `${exp.width.toLocaleString('fr-FR')} × ${exp.height.toLocaleString('fr-FR')} px · ≈ ${Math.round(exp.actualDpi)} ppp`;
    }

    computeExportSpec() {
      const s = this.getPrintSize();
      const wi = cmToIn(s.widthCm), hi = cmToIn(s.heightCm);
      let w = Math.round(wi * this.exportDpi), h = Math.round(hi * this.exportDpi);
      const maxPixels = Math.max(5, this.maxExportMp) * 1e6;
      const px = w * h;
      if (px > maxPixels) {
        const k = Math.sqrt(maxPixels / px);
        w = Math.floor(w * k); h = Math.floor(h * k);
      }
      return { width: w, height: h, actualDpi: Math.min(w / wi, h / hi) };
    }

    async handleFiles(files) {
      if (this.reviewOpen || this.preparingReview || this.importing || this.saving) return;
      this.clearStatus();
      if (!files.length) return;
      const currentImages = this.canvas.getObjects().filter(o => o.type === 'image').length;
      if (currentImages + files.length > this.maxImages) {
        this.showStatus(`Maximum ${this.maxImages} images par création.`, 'error');
        this.els.fileInput.value = '';
        return;
      }
      this.importing = true;
      this.refreshAddState();
      for (const file of files) {
        try { await this.addImageFile(file); }
        catch (e) { this.showStatus(e.message || 'Impossible d’ajouter cette image.', 'error'); }
      }
      this.importing = false;
      this.els.fileInput.value = '';
      this.renderFileList();
      this.updateQuality();
      this.pushHistory();
    }

    async addImageFile(file) {
      if (!IMAGE_TYPES.includes(file.type) && !file.type.startsWith('image/')) throw new Error(`${file.name} : format non reconnu. Utilisez JPG, PNG, WebP ou HEIC.`);
      if (file.size > this.maxInputMb * 1024 * 1024) throw new Error(`${file.name} : fichier trop lourd. Maximum ${this.maxInputMb} Mo.`);
      const url = URL.createObjectURL(file);
      let htmlImg;
      try { htmlImg = await loadHtmlImage(url); }
      catch (_) { URL.revokeObjectURL(url); throw new Error(`${file.name} : cette image ne peut pas être lue par votre navigateur.`); }
      if (Math.min(htmlImg.naturalWidth, htmlImg.naturalHeight) < this.absoluteMinPx) {
        URL.revokeObjectURL(url);
        throw new Error(`${file.name} : image trop petite (${htmlImg.naturalWidth} × ${htmlImg.naturalHeight} px). Minimum absolu : ${this.absoluteMinPx} px sur le petit côté.`);
      }
      const assetId = uid();
      this.assets.set(assetId, { file, url, name: file.name, width: htmlImg.naturalWidth, height: htmlImg.naturalHeight });
      const img = new this.fabric.Image(htmlImg, {
        left: this.canvas.getWidth()/2,
        top: this.canvas.getHeight()/2,
        originX: 'center', originY: 'center',
        _assetId: assetId, _srcWidth: htmlImg.naturalWidth, _srcHeight: htmlImg.naturalHeight, _assetName: file.name
      });
      const scale = Math.min((this.canvas.getWidth() * .78) / img.width, (this.canvas.getHeight() * .78) / img.height, 1);
      img.scale(scale);
      img.setControlsVisibility({ mt:false, mb:false, ml:false, mr:false });
      this.canvas.add(img);
      this.canvas.setActiveObject(img);
      this.canvas.requestRenderAll();
    }

    renderFileList() {
      const imageObjects = this.canvas.getObjects().filter(o => o.type === 'image');
      this.els.fileList.innerHTML = '';
      if (this.els.photoCount) this.els.photoCount.textContent = `${imageObjects.length} / ${this.maxImages}`;
      imageObjects.forEach(o => {
        const row = document.createElement('div');
        row.className = 'ah-filechip';
        row.dataset.assetId = o._assetId || '';
        row.tabIndex = 0;
        row.setAttribute('role','button');
        row.setAttribute('aria-label', `Sélectionner ${o._assetName || 'cette image'}`);

        const thumb = document.createElement('div'); thumb.className = 'ah-filechip__thumb';
        const img = document.createElement('img'); img.alt = ''; img.loading = 'lazy';
        const asset = this.assets.get(o._assetId);
        if (asset?.url) img.src = asset.url;
        else if (typeof o.getSrc === 'function') img.src = o.getSrc();
        thumb.appendChild(img);

        const meta = document.createElement('div'); meta.className = 'ah-filechip__meta';
        const name = document.createElement('span'); name.className = 'ah-filechip__name'; name.textContent = o._assetName || 'Image';
        const dpi = document.createElement('span'); dpi.className = 'ah-filechip__dpi'; dpi.textContent = `${Math.round(this.getImageDpi(o))} ppp dans la composition`;
        meta.append(name, dpi);

        const btn = document.createElement('button'); btn.type = 'button'; btn.setAttribute('aria-label','Retirer cette image');
        btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
        btn.addEventListener('click', e => { e.stopPropagation(); this.canvas.remove(o); this.canvas.requestRenderAll(); this.renderFileList(); this.updateQuality(); this.pushHistory(); });
        const select = () => { this.canvas.setActiveObject(o); this.canvas.requestRenderAll(); this.syncSelectedAsset(); };
        row.addEventListener('click', select);
        row.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } });
        row.append(thumb, meta, btn); this.els.fileList.append(row);
      });
      this.syncSelectedAsset();
    }

    syncSelectedAsset() {
      const active = this.canvas.getActiveObject();
      const id = active?.type === 'image' ? active._assetId : '';
      this.els.fileList.querySelectorAll('[data-asset-id]').forEach(el => el.classList.toggle('is-selected', !!id && el.dataset.assetId === id));
    }

    addText() {
      const value = this.els.textInput.value.trim();
      if (!value) return;
      const t = new this.fabric.IText(value, {
        left: this.canvas.getWidth()/2, top: this.canvas.getHeight()/2,
        originX:'center', originY:'center', fill:'#2d2a24', fontFamily:'Arial', fontSize: Math.max(24, this.canvas.getWidth()*.055)
      });
      this.canvas.add(t); this.canvas.setActiveObject(t); this.canvas.requestRenderAll();
      this.els.textInput.value = '';
      this.pushHistory();
    }

    toggleDraw() {
      this.canvas.isDrawingMode = !this.canvas.isDrawingMode;
      this.els.draw.classList.toggle('is-active', this.canvas.isDrawingMode);
      if (this.els.drawLabel) this.els.drawLabel.textContent = this.canvas.isDrawingMode ? 'Quitter le stylo' : 'Activer le stylo';
      if (this.canvas.isDrawingMode) this.canvas.discardActiveObject().requestRenderAll();
    }

    deleteSelection() {
      const active = this.canvas.getActiveObject();
      if (!active) return;
      if (active.type === 'activeSelection') active.forEachObject(o => this.canvas.remove(o));
      else this.canvas.remove(active);
      this.canvas.discardActiveObject(); this.canvas.requestRenderAll();
      this.renderFileList(); this.updateQuality(); this.pushHistory();
    }

    fitSelectedImage() {
      const o = this.canvas.getActiveObject();
      if (!o || o.type !== 'image') { this.showStatus('Sélectionnez d’abord une image.', 'info'); return; }
      const scale = Math.max(this.canvas.getWidth()/o.width, this.canvas.getHeight()/o.height);
      o.scale(scale); o.set({left:this.canvas.getWidth()/2, top:this.canvas.getHeight()/2, originX:'center', originY:'center'}); o.setCoords();
      this.canvas.requestRenderAll(); this.pushHistory(); this.updateQuality();
    }

    clearDesign() {
      if (!this.canvas.getObjects().length) return;
      if (!confirm('Effacer toute la création ?')) return;
      this.canvas.getObjects().slice().forEach(o => this.canvas.remove(o));
      this.canvas.discardActiveObject(); this.canvas.requestRenderAll();
      this.renderFileList(); this.updateQuality(); this.pushHistory();
    }

    getImageDpi(obj) {
      const s = this.getPrintSize();
      const printedWIn = cmToIn(s.widthCm) * (obj.getScaledWidth() / this.canvas.getWidth());
      const printedHIn = cmToIn(s.heightCm) * (obj.getScaledHeight() / this.canvas.getHeight());
      const srcW = Number(obj._srcWidth || obj.width || 1), srcH = Number(obj._srcHeight || obj.height || 1);
      return Math.min(srcW / Math.max(.01, printedWIn), srcH / Math.max(.01, printedHIn));
    }

    updateQuality() {
      const images = this.canvas.getObjects().filter(o => o.type === 'image');
      this.els.quality.innerHTML = '';
      let blocking = false;
      if (!images.length) {
        const empty = document.createElement('div'); empty.className = 'ah-empty'; empty.textContent = 'Ajoutez une image pour afficher son contrôle qualité.';
        this.els.quality.appendChild(empty);
      }
      images.forEach((o, idx) => {
        const dpi = this.getImageDpi(o);
        const state = dpi < this.minDpi ? 'bad' : dpi < this.recDpi ? 'warn' : 'ok';
        if (state === 'bad') blocking = true;
        const row = document.createElement('div'); row.className = 'ah-qitem';
        row.innerHTML = `<div class="ah-qitem__row"><span class="ah-qitem__name">${safeText(o._assetName || `Image ${idx+1}`)}</span><span class="ah-badge ah-badge--${state}">${Math.round(dpi)} ppp</span></div><small>${state==='bad' ? `Trop faible : réduisez l’image dans la composition ou importez un fichier plus grand. Minimum ${this.minDpi} ppp.` : state==='warn' ? `Accepté, mais ${this.recDpi} ppp ou plus est recommandé.` : 'Très bonne définition pour la taille utilisée.'}</small>`;
        this.els.quality.appendChild(row);
      });
      this.hasBlockingQuality = blocking;
      if (this.els.qualityGlobal) {
        const dpis = images.map(o => this.getImageDpi(o));
        const hasWarn = dpis.some(dpi => dpi >= this.minDpi && dpi < this.recDpi);
        this.els.qualityGlobal.className = 'ah-quality-global';
        if (!images.length) this.els.qualityGlobal.textContent = 'En attente';
        else if (blocking) { this.els.qualityGlobal.textContent = 'À corriger'; this.els.qualityGlobal.classList.add('is-bad'); }
        else if (hasWarn) { this.els.qualityGlobal.textContent = 'Correcte'; this.els.qualityGlobal.classList.add('is-warn'); }
        else { this.els.qualityGlobal.textContent = 'Excellente'; this.els.qualityGlobal.classList.add('is-ok'); }
      }
      this.els.fileList.querySelectorAll('[data-asset-id]').forEach(card => {
        const obj = images.find(o => o._assetId === card.dataset.assetId);
        const label = card.querySelector('.ah-filechip__dpi');
        if (obj && label) label.textContent = `${Math.round(this.getImageDpi(obj))} ppp dans la composition`;
      });
      this.refreshAddState();
    }

    refreshAddState() {
      const hasDesign = this.canvas.getObjects().length > 0;
      const variantOk = !!this.selectedVariant?.available;
      const busy = !!(this.saving || this.addedToCart || this.preparingReview || this.importing || this.loadingHistory);
      const canReview = hasDesign && variantOk && !busy;
      this.els.add.disabled = !canReview;
      this.els.mobileReview.forEach(button => { button.disabled = !canReview; });
      this.els.reviewAdd.disabled = !this.reviewOpen || !canReview || this.hasBlockingQuality || !this.els.consent.checked;
      this.els.reviewEdit.forEach(button => { button.disabled = !!(this.saving || this.addedToCart); });
      this.els.review.setAttribute('aria-busy', String(!!this.saving));
      this.els.editorShell.inert = !!(this.reviewOpen || this.preparingReview);
    }

    updateSummary() {
      const s = this.getPrintSize();
      this.els.summaryFormat.textContent = `${s.widthCm} × ${s.heightCm} cm`;
      this.els.summaryOrientation.textContent = this.getOrientationLabel();
      this.els.orientation.querySelectorAll('[data-value]').forEach(btn => {
        btn.disabled = s.widthCm === s.heightCm;
        btn.classList.toggle('is-active', !btn.disabled && btn.dataset.value === (s.widthCm > s.heightCm ? 'landscape' : 'portrait'));
      });
      this.updateFramePreview();
      this.els.summaryVariant.textContent = this.selectedVariant?.title || '—';
      const formatted = this.selectedVariant ? (this.selectedVariant.formattedPrice || money(this.selectedVariant.price, this.currency)) : '—';
      this.els.prices.forEach(el => { el.textContent = formatted; });
      this.refreshAddState();
    }

    debouncedHistory() {
      clearTimeout(this._historyTimer);
      this._historyTimer = setTimeout(() => this.pushHistory(), 120);
    }

    serialize() {
      return JSON.stringify({ canvas: this.canvas.toJSON(['_assetId','_srcWidth','_srcHeight','_assetName']), options: this.selectedOptions, orientation: this.orientation, bgColor: this.bgColor });
    }

    pushHistory() {
      if (this.loadingHistory) return;
      const json = this.serialize();
      if (this.history[this.historyIndex] === json) return;
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(json);
      if (this.history.length > 25) this.history.shift();
      this.historyIndex = this.history.length - 1;
      this.els.undo.disabled = this.historyIndex <= 0;
      this.els.redo.disabled = true;
    }

    async loadHistory(index) {
      if (this.loadingHistory || this.reviewOpen || this.preparingReview || this.saving || index < 0 || index >= this.history.length) return;
      clearTimeout(this._historyTimer);
      this.loadingHistory = true;
      const state = JSON.parse(this.history[index]);
      this.selectedOptions = [...state.options];
      this.selectedVariant = this.findExactVariant(this.selectedOptions);
      this.orientation = state.orientation;
      this.bgColor = state.bgColor;
      this.els.bg.value = this.bgColor;
      this.resizeCanvasForCurrentFormat(false);
      await new Promise(resolve => this.canvas.loadFromJSON(state.canvas, () => { this.canvas.renderAll(); resolve(); }));
      this.syncOptionButtons(); this.updateOptionAvailability(); this.updateRequirements(); this.updateSummary();
      this.loadingHistory = false;
      this.historyIndex = index;
      this.els.undo.disabled = this.historyIndex <= 0;
      this.els.redo.disabled = this.historyIndex >= this.history.length - 1;
      this.renderFileList(); this.updateQuality();
    }
    undo() { if (this.historyIndex > 0) this.loadHistory(this.historyIndex - 1); }
    redo() { if (this.historyIndex < this.history.length - 1) this.loadHistory(this.historyIndex + 1); }

    async generatePrintFile() {
      this.canvas.discardActiveObject();
      const prevDrawing = this.canvas.isDrawingMode;
      this.canvas.isDrawingMode = false;
      this.canvas.requestRenderAll();
      let spec = this.computeExportSpec();
      let attempts = 0, blob = null, outCanvas = null;
      while (attempts < 4 && !blob) {
        attempts++;
        const multiplier = spec.width / this.canvas.getWidth();
        try {
          const rendered = this.canvas.toCanvasElement(multiplier);
          outCanvas = document.createElement('canvas');
          outCanvas.width = spec.width;
          outCanvas.height = spec.height;
          outCanvas.getContext('2d').drawImage(rendered, 0, 0, outCanvas.width, outCanvas.height);
          rendered.width = 0; rendered.height = 0;
          blob = await canvasToBlob(outCanvas, 'image/jpeg', 0.94);
        } catch (e) {
          spec.width = Math.floor(spec.width * .82);
          spec.height = Math.floor(spec.height * .82);
        }
      }
      this.canvas.isDrawingMode = prevDrawing;
      if (!blob) throw new Error('Votre appareil n’a pas assez de mémoire pour générer ce format. Essayez depuis un ordinateur ou choisissez un format plus petit.');
      if (blob.size > 19.5 * 1024 * 1024) {
        blob = await canvasToBlob(outCanvas, 'image/jpeg', 0.86);
      }
      if (blob.size > 20 * 1024 * 1024) throw new Error('Le fichier final dépasse 20 Mo. Réduisez le nombre d’images ou choisissez un format plus petit.');
      const size = this.getPrintSize();
      spec.actualDpi = Math.min(spec.width / cmToIn(size.widthCm), spec.height / cmToIn(size.heightCm));
      const filename = `ambient-home-${this.designId}-${spec.width}x${spec.height}.jpg`;
      return { file: new File([blob], filename, { type:'image/jpeg' }), spec };
    }

    async finalizeAndAdd() {
      if (this.saving || this.addedToCart || this.preparingReview) return;
      if (!this.reviewOpen) { await this.openReview(); return; }
      if (this.reviewSignature !== this.serialize()) { this.leaveReview(); this.showStatus('Votre création a changé. Vérifiez à nouveau votre tableau avant de commander.', 'info'); return; }
      this.updateQuality();
      if (this.hasBlockingQuality) { this.showStatus(`Une ou plusieurs images sont sous ${this.minDpi} ppp. Réduisez leur taille dans le visuel ou importez une meilleure photo.`, 'error'); return; }
      if (!this.canvas.getObjects().length) { this.showStatus('Ajoutez au moins une image, un texte ou un dessin.', 'error'); return; }
      if (!this.selectedVariant?.available) { this.showStatus('La variante choisie n’est pas disponible.', 'error'); return; }
      if (!this.els.consent.checked) { this.showStatus('Confirmez que vous disposez des droits sur les images utilisées.', 'error'); return; }

      this.saving = true; this.refreshAddState(); this.showStatus('Préparation du fichier haute définition…', 'info');
      try {
        const { file, spec } = await this.generatePrintFile();
        this.showStatus('Envoi sécurisé de votre création vers Shopify…', 'info');
        const s = this.getPrintSize();
        const fd = new FormData();
        fd.append('id', String(this.selectedVariant.id));
        fd.append('quantity', '1');
        fd.append('properties[Personnalisation]', 'Création sur mesure');
        fd.append('properties[Format]', `${s.widthCm} × ${s.heightCm} cm`);
        fd.append('properties[Orientation]', this.getOrientationLabel());
        fd.append('properties[_Design ID]', this.designId);
        fd.append('properties[_Résolution export]', `${spec.width} × ${spec.height} px`);
        fd.append('properties[_PPP export]', String(Math.round(spec.actualDpi)));
        if (this.sourceProduct) fd.append('properties[_Produit source]', this.sourceProduct);
        fd.append('properties[_Fichier impression]', file, file.name);

        const url = (window.Shopify?.routes?.root || '/') + 'cart/add.js';
        const res = await fetch(url, { method:'POST', body:fd, headers:{'Accept':'application/json'} });
        if (!res.ok) {
          let msg = 'Impossible d’ajouter le tableau au panier.';
          try { const data = await res.json(); msg = data.description || data.message || msg; } catch(_) {}
          throw new Error(msg);
        }
        this.addedToCart = true;
        this.showStatus('Création ajoutée au panier. Redirection…', 'ok');
        setTimeout(() => { location.href = (window.Shopify?.routes?.root || '/') + 'cart'; }, 450);
      } catch (e) {
        console.error('[Ambient Home personnalisateur]', e);
        this.showStatus(e.message || 'Une erreur est survenue pendant l’enregistrement.', 'error');
      } finally {
        this.saving = false; this.refreshAddState();
      }
    }

    showStatus(message, type='info') {
      const status = this.reviewOpen ? this.els.reviewStatus : this.els.status;
      status.textContent = message;
      status.className = `ah-status ah-status--${type} is-visible`;
      if (!this.reviewOpen) status.scrollIntoView({behavior:'smooth',block:'nearest'});
    }
    clearStatus() { [this.els.status, this.els.reviewStatus].forEach(status => { status.textContent = ''; status.className = 'ah-status'; }); }
  }

  async function boot() {
    const roots = [...document.querySelectorAll(ROOT_SELECTOR)];
    if (!roots.length) return;
    try {
      const fabric = await waitForFabric();
      roots.forEach(root => {
        if (root.dataset.ahInitialized === BUILD) return;
        try {
          root.dataset.ahInitialized = BUILD;
          new AHCustomizer(root, fabric);
        } catch (e) {
          delete root.dataset.ahInitialized;
          console.error(`[Ambient Home personnalisateur ${BUILD}]`, e);
          const s = root.querySelector('[data-ah-status]');
          if (s) { s.textContent = e.message; s.className = 'ah-status ah-status--error is-visible'; }
        }
      });
    } catch (e) {
      roots.forEach(root => { const s = root.querySelector('[data-ah-status]'); if (s) { s.textContent = e.message; s.className = 'ah-status ah-status--error is-visible'; } });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', () => boot());
})();
